// LazyChart.jsx - Complete component for lazy loading individual charts
import React, { useState, useEffect, useRef } from 'react';
import ChartRenderer from './ChartRenderer';
import { ChartSkeleton, MultiBarChartSkeleton } from './GraphSkelton';

const LazyChart = ({ 
  chartConfig, 
  chartState, 
  vehiclesData, 
  selectedDateRange,
  isTimelineChart = false 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    // Don't set up observer if already been visible
    if (hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When element comes into viewport, set it as visible
        if (entry.isIntersecting && !hasBeenVisible) {
          setIsVisible(true);
          setHasBeenVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of element is visible
        rootMargin: '100px', // Start loading 100px before entering viewport
        // Add root margin to trigger earlier
      }
    );

    const currentRef = chartRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    // Cleanup function
    return () => {
      if (observer && currentRef) {
        observer.unobserve(currentRef);
        observer.disconnect();
      }
    };
  }, [hasBeenVisible]);

  // Determine which skeleton to show based on chart type
  const renderSkeleton = () => {
    // Multi-bar charts use different skeleton
    if (
      chartConfig.id === "total_halt" ||
      chartConfig.id === "halt_duration" ||
      chartConfig.id === "total_trips"
    ) {
      return <MultiBarChartSkeleton />;
    }
    
    // Default skeleton based on chart type
    return (
      <ChartSkeleton
        chartType={chartConfig.chartType || 'barGraph'}
        title={chartConfig.title}
      />
    );
  };

  return (
    <div 
      ref={chartRef} 
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: isTimelineChart ? '400px' : '350px'
      }}
    >
      {isVisible || hasBeenVisible ? (
        <ChartRenderer
          chartConfig={chartConfig}
          chartState={chartState}
          vehiclesData={vehiclesData}
          selectedDateRange={selectedDateRange}
        />
      ) : (
        renderSkeleton()
      )}
    </div>
  );
};

export default LazyChart;